import { useState, useMemo } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { Check, ChevronDown, ChevronUp, Link2, X } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible'
import { useArticles, useUpdateArticle } from '@/hooks/queries'
import { cn } from '@/lib/utils'
import type { Article, ContentStatus } from '@/lib/types'

// ─── Types ────────────────────────────────────────────────────────────────────

type TabStatus = 'draft' | 'published' | 'suppressed' | 'archived'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function severityClass(severity: Article['severity']) {
  switch (severity) {
    case 'critical':
      return 'bg-red-500/15 text-red-400 border-red-500/30'
    case 'high':
      return 'bg-orange-500/15 text-orange-400 border-orange-500/30'
    case 'medium':
      return 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'
    case 'low':
      return 'bg-blue-500/15 text-blue-400 border-blue-500/30'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function categoryLabel(cat: Article['category']) {
  return cat?.replace(/_/g, ' ') ?? '—'
}

// ─── Risk badge ───────────────────────────────────────────────────────────────

function RiskBadge({ score }: { score: number }) {
  const variant =
    score >= 80 ? 'destructive' : score >= 60 ? 'secondary' : 'outline'
  return (
    <Badge variant={variant} className="tabular-nums text-xs">
      {score}
    </Badge>
  )
}

// ─── Checkbox ─────────────────────────────────────────────────────────────────

function Checkbox({
  checked,
  onCheckedChange,
  'aria-label': ariaLabel,
}: {
  checked: boolean
  onCheckedChange: (v: boolean) => void
  'aria-label'?: string
}) {
  return (
    <CheckboxPrimitive.Root
      checked={checked}
      onCheckedChange={(v) => onCheckedChange(v === true)}
      aria-label={ariaLabel}
      className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-input bg-background ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
    >
      <CheckboxPrimitive.Indicator>
        <Check className="h-3 w-3" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

// ─── Edit modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  article: Article | null
  onClose: () => void
}

function EditModal({ article, onClose }: EditModalProps) {
  const [title, setTitle] = useState(article?.title ?? '')
  const [summary, setSummary] = useState(article?.summary ?? '')
  const [status, setStatus] = useState<ContentStatus>(
    article?.status ?? 'draft',
  )
  const updateArticle = useUpdateArticle()

  function handleSave() {
    if (!article) return
    updateArticle.mutate(
      { id: article.id, title, summary, status },
      { onSuccess: onClose },
    )
  }

  return (
    <DialogPrimitive.Root open={Boolean(article)} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
            <div>
              <DialogPrimitive.Title className="text-base font-semibold">
                Edit Article
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-xs text-muted-foreground mt-0.5">
                Update title, summary, and moderation status.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-4 px-6 py-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Title</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Article title"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Summary</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={4}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                placeholder="Article summary"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as ContentStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="suppressed">Suppressed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t px-6 py-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={updateArticle.isPending}>
              {updateArticle.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// ─── Bulk action bar ──────────────────────────────────────────────────────────

interface BulkBarProps {
  selectedIds: Set<string>
  activeTab: TabStatus
  onBulkAction: (status: ContentStatus) => void
}

function BulkBar({ selectedIds, activeTab, onBulkAction }: BulkBarProps) {
  const count = selectedIds.size
  const disabled = count === 0

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">
        {count > 0 ? `${count} selected` : 'No selection'}
      </span>
      {activeTab === 'draft' && (
        <>
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            className="text-green-400 border-green-500/40 hover:bg-green-500/10 disabled:opacity-40"
            onClick={() => onBulkAction('published')}
          >
            Bulk Publish
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            className="text-orange-400 border-orange-500/40 hover:bg-orange-500/10 disabled:opacity-40"
            onClick={() => onBulkAction('suppressed')}
          >
            Bulk Suppress
          </Button>
        </>
      )}
      {activeTab === 'published' && (
        <>
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            className="text-orange-400 border-orange-500/40 hover:bg-orange-500/10 disabled:opacity-40"
            onClick={() => onBulkAction('suppressed')}
          >
            Bulk Suppress
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            className="text-muted-foreground hover:bg-muted/50 disabled:opacity-40"
            onClick={() => onBulkAction('archived')}
          >
            Bulk Archive
          </Button>
        </>
      )}
      {activeTab === 'suppressed' && (
        <>
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            className="text-green-400 border-green-500/40 hover:bg-green-500/10 disabled:opacity-40"
            onClick={() => onBulkAction('published')}
          >
            Bulk Publish
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            className="text-muted-foreground hover:bg-muted/50 disabled:opacity-40"
            onClick={() => onBulkAction('archived')}
          >
            Bulk Archive
          </Button>
        </>
      )}
      {activeTab === 'archived' && (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          className="text-blue-400 border-blue-500/40 hover:bg-blue-500/10 disabled:opacity-40"
          onClick={() => onBulkAction('draft')}
        >
          Bulk Restore to Draft
        </Button>
      )}
    </div>
  )
}

// ─── Article table ────────────────────────────────────────────────────────────

interface ArticleTableProps {
  articles: Article[]
  isLoading: boolean
  activeTab: TabStatus
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onToggleAll: (ids: string[]) => void
  onAction: (id: string, status: ContentStatus) => void
  onEdit: (article: Article) => void
}

function ArticleTable({
  articles,
  isLoading,
  activeTab,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onAction,
  onEdit,
}: ArticleTableProps) {
  const allSelected =
    articles.length > 0 && articles.every((a) => selectedIds.has(a.id))

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  if (articles.length === 0) {
    return (
      <p className="text-sm text-muted-foreground p-6">
        No articles in this queue.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-muted-foreground">
            <th className="px-4 py-3 w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(v) =>
                  v
                    ? onToggleAll(articles.map((a) => a.id))
                    : onToggleAll([])
                }
                aria-label="Select all"
              />
            </th>
            <th className="px-4 py-3 text-left font-medium">Title</th>
            <th className="px-4 py-3 text-left font-medium hidden sm:table-cell">
              Category
            </th>
            <th className="px-4 py-3 text-center font-medium hidden md:table-cell">
              Severity
            </th>
            <th className="px-4 py-3 text-center font-medium">Risk</th>
            <th className="px-4 py-3 text-left font-medium hidden lg:table-cell">
              Publisher
            </th>
            <th className="px-4 py-3 text-right font-medium hidden lg:table-cell">
              Date
            </th>
            <th className="px-4 py-3 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {articles.map((article) => (
            <tr
              key={article.id}
              className={cn(
                'border-b last:border-0 hover:bg-muted/30 transition-colors',
                selectedIds.has(article.id) && 'bg-muted/20',
              )}
            >
              <td className="px-4 py-3">
                <Checkbox
                  checked={selectedIds.has(article.id)}
                  onCheckedChange={() => onToggleSelect(article.id)}
                  aria-label={`Select ${article.title}`}
                />
              </td>
              <td className="px-4 py-3 max-w-xs">
                <button
                  className="line-clamp-2 text-left font-medium hover:underline hover:text-foreground/80 transition-colors"
                  onClick={() => onEdit(article)}
                >
                  {article.title}
                </button>
              </td>
              <td className="px-4 py-3 hidden sm:table-cell">
                <span className="text-muted-foreground capitalize text-xs">
                  {categoryLabel(article.category)}
                </span>
              </td>
              <td className="px-4 py-3 text-center hidden md:table-cell">
                {article.severity ? (
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${severityClass(article.severity)}`}
                  >
                    {article.severity}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-center">
                <RiskBadge score={article.riskScore} />
              </td>
              <td className="px-4 py-3 hidden lg:table-cell">
                <span className="text-muted-foreground text-xs truncate max-w-[120px] block">
                  {article.publisher}
                </span>
              </td>
              <td className="px-4 py-3 text-right hidden lg:table-cell">
                <span className="text-muted-foreground text-xs">
                  {new Date(article.publishedAt).toLocaleDateString()}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-1.5">
                  {activeTab === 'draft' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-green-400 border-green-500/40 hover:bg-green-500/10"
                        onClick={() => onAction(article.id, 'published')}
                      >
                        Publish
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-orange-400 border-orange-500/40 hover:bg-orange-500/10"
                        onClick={() => onAction(article.id, 'suppressed')}
                      >
                        Suppress
                      </Button>
                    </>
                  )}
                  {activeTab === 'published' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-orange-400 border-orange-500/40 hover:bg-orange-500/10"
                        onClick={() => onAction(article.id, 'suppressed')}
                      >
                        Suppress
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-muted-foreground hover:bg-muted/50"
                        onClick={() => onAction(article.id, 'archived')}
                      >
                        Archive
                      </Button>
                    </>
                  )}
                  {activeTab === 'suppressed' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-green-400 border-green-500/40 hover:bg-green-500/10"
                        onClick={() => onAction(article.id, 'published')}
                      >
                        Publish
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-muted-foreground hover:bg-muted/50"
                        onClick={() => onAction(article.id, 'archived')}
                      >
                        Archive
                      </Button>
                    </>
                  )}
                  {activeTab === 'archived' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs text-blue-400 border-blue-500/40 hover:bg-blue-500/10"
                      onClick={() => onAction(article.id, 'draft')}
                    >
                      Restore to Draft
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Manual submission panel ──────────────────────────────────────────────────

function ManualSubmissionPanel() {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [content, setContent] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  function handleFetch() {
    setNotice(`URL fetch is not yet implemented. Entered URL: ${url}`)
  }

  function handleSubmit() {
    setNotice('Manual submission is not yet implemented.')
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border">
        <CollapsibleTrigger asChild>
          <button className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/30 transition-colors">
            <span className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-muted-foreground" />
              Submit article manually
            </span>
            {open ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-4 border-t px-4 py-4">
            {notice && (
              <div className="rounded-md bg-muted/50 border px-3 py-2 text-sm text-muted-foreground">
                {notice}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Article URL</label>
              <div className="flex gap-2">
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/article"
                  className="flex-1"
                />
                <Button variant="outline" onClick={handleFetch}>
                  Fetch
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Paste content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={5}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                placeholder="Paste article text here…"
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSubmit}>Submit</Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ContentModerationPage() {
  const [activeTab, setActiveTab] = useState<TabStatus>('draft')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [editingArticle, setEditingArticle] = useState<Article | null>(null)

  const qc = useQueryClient()
  const { data: allArticles, isLoading } = useArticles({ limit: 500 })
  const updateArticle = useUpdateArticle()

  // Split into buckets
  const byStatus = useMemo(() => {
    const base = allArticles ?? []
    return {
      draft: base.filter((a) => a.status === 'draft'),
      published: base.filter((a) => a.status === 'published'),
      suppressed: base.filter((a) => a.status === 'suppressed'),
      archived: base.filter((a) => a.status === 'archived'),
    }
  }, [allArticles])

  const activeArticles = byStatus[activeTab]

  // Selection helpers
  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll(ids: string[]) {
    if (ids.length === 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(ids))
    }
  }

  // Single action
  function handleAction(id: string, status: ContentStatus) {
    updateArticle.mutate(
      { id, status },
      { onSuccess: () => qc.invalidateQueries({ queryKey: ['articles'] }) },
    )
  }

  // Bulk action
  async function handleBulkAction(status: ContentStatus) {
    const ids = Array.from(selectedIds)
    await Promise.all(
      ids.map(
        (id) =>
          new Promise<void>((resolve, reject) => {
            updateArticle.mutate({ id, status }, { onSuccess: () => resolve(), onError: reject })
          })
      )
    )
    setSelectedIds(new Set())
    qc.invalidateQueries({ queryKey: ['articles'] })
  }

  // Clear selection on tab change
  function handleTabChange(tab: string) {
    setActiveTab(tab as TabStatus)
    setSelectedIds(new Set())
  }

  const tabs: { value: TabStatus; label: string }[] = [
    { value: 'draft', label: 'Drafts' },
    { value: 'published', label: 'Published' },
    { value: 'suppressed', label: 'Suppressed' },
    { value: 'archived', label: 'Archived' },
  ]

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Content Moderation
        </h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          Review and manage intelligence items
        </p>
      </div>

      {/* Manual submission */}
      <ManualSubmissionPanel />

      {/* Tabs + table */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <TabsList>
            {tabs.map(({ value, label }) => {
              const count = isLoading ? null : byStatus[value].length
              return (
                <TabsTrigger key={value} value={value} className="gap-1.5">
                  {label}
                  {count !== null && (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
                      {count}
                    </span>
                  )}
                </TabsTrigger>
              )
            })}
          </TabsList>

          <BulkBar
            selectedIds={selectedIds}
            activeTab={activeTab}
            onBulkAction={handleBulkAction}
          />
        </div>

        {tabs.map(({ value }) => (
          <TabsContent key={value} value={value}>
            <div className="rounded-lg border mt-3">
              <ArticleTable
                articles={activeArticles}
                isLoading={isLoading}
                activeTab={activeTab}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleAll={toggleAll}
                onAction={handleAction}
                onEdit={setEditingArticle}
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Edit modal */}
      <EditModal
        article={editingArticle}
        onClose={() => setEditingArticle(null)}
      />
    </div>
  )
}
